/**
 * O despachante: dado um nó, monta o componente que o desenha.
 *
 * Substitui o `ngSwitch` aninhado em quatro níveis de `canvas.component.html`
 * (1.817 linhas), onde o tipo `text` aparecia escrito quatro vezes — nas linhas
 * 68, 176, 527 e no `switchDefault` — e as quatro divergiam entre si.
 *
 * A recursão é a chave: cada renderer de container desenha `<tem-node-host>`
 * para os filhos dele. Profundidade arbitrária, sem duplicação, e um tipo novo
 * não toca em nenhum template existente.
 *
 * **A forma do template não é acidental.** Em produção o nó é desenhado SEM
 * invólucro: nenhuma `div` a mais, nenhuma classe, nenhum ouvinte de ponteiro. O
 * invólucro de edição só existe quando `EDITOR_CHROME` está provido — e é essa a
 * diferença entre "esconder as ferramentas" e "não ter ferramentas".
 */
import {
  ChangeDetectionStrategy,
  Component,
  Type,
  computed,
  inject,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';

import { ScreenNode } from '../model/node-props';
import { SCREEN_RUNTIME } from '../runtime/screen-runtime';
import { EDITOR_CHROME } from './editor-chrome';
import { ScreenContext } from './screen-context';
import { movedDetailPanelIds } from './detail/detail-panels';
import { NODE_RENDERERS, NodeRendererInputs, RenderMode } from './node-renderer';

@Component({
  selector: 'tem-node-host',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      @if (chrome !== null) {
        <div
          class="tem-node"
          [ngClass]="chromeClasses()"
          [attr.data-node-id]="node().id"
          [attr.data-node-type]="node().type"
          (pointerdown)="onPointerDown($event)">
          <ng-container [ngTemplateOutlet]="rendered" />

          <!-- A sobreposicao do editor: alcas, barra de acao. Fica DEPOIS do
               conteudo para desenhar por cima, e so existe quando o editor pede
               uma para este no. Em producao nem este ramo do template e criado. -->
          @if (overlayType(); as overlay) {
            <ng-container *ngComponentOutlet="overlay; inputs: overlayInputs()" />
          }
        </div>
      } @else {
        <ng-container [ngTemplateOutlet]="rendered" />
      }
    }

    <ng-template #rendered>
      <ng-container *ngComponentOutlet="rendererType(); inputs: rendererInputs()" />
    </ng-template>
  `,
  styles: [`
    /* Só se aplica quando o editor está montado. Em produção este bloco não
       encontra elemento nenhum, porque o invólucro não é criado. */
    .tem-node {
      position: relative;
      border-radius: var(--tem-radius);
      outline-offset: -1px;
    }

    .tem-node--hoverable:hover {
      outline: 1px dashed var(--tem-border);
    }

    .tem-node--selected {
      outline: 2px solid var(--tem-accent);
    }

    /* A ALTURA RESERVADA, enquanto o nó está selecionado.
       "Altura em linhas" reserva espaço na grade, e até aqui o único jeito de
       saber quanto foi reservado era olhar o buraco embaixo do componente. A
       marca aparece só na seleção — não suja a tela, e em produção não existe
       seleção nenhuma, então ela nunca chega ao usuário final. */
    .tem-node--selected::before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      border: 1px dashed var(--tem-accent);
      border-radius: var(--tem-radius);
      opacity: .5;
    }

    .tem-node--invalid {
      outline: 2px solid var(--tem-danger);
    }

    /* O nó que está sendo arrastado, no lugar de ORIGEM. Fica apagado para o
       usuário ver que ele vai sair dali. Como as outras classes deste bloco,
       só existe quando o editor está montado. */
    .tem-node--dragging {
      opacity: .35;
    }

    /* Nó escondido por regra continua visível no editor, mas precisa parecer
       escondido — senão o usuário configura um visibleWhen e não entende por
       que o campo continua ali. */
    .tem-node--hidden-by-rule {
      opacity: .45;
    }

    /* Selo de comportamento: sem descoberta visual, ninguém sabe que a tela tem
       lógica configurada, e é preciso abrir componente por componente. */
    .tem-node--has-behavior::after {
      content: '';
      position: absolute;
      top: 2px;
      right: 2px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--tem-warn);
    }
  `]
})
export class NodeHostComponent {

  readonly node = input.required<ScreenNode>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  private readonly renderers = inject(NODE_RENDERERS);

  /**
   * Em produção isto é `null`, e todo o custo de edição desaparece com ele —
   * não há invólucro para criar, decoração para calcular nem ponteiro para
   * escutar. É a diferença entre a tela publicada pagar ou não pelo editor.
   */
  readonly chrome = inject(EDITOR_CHROME, { optional: true });

  /**
   * O runtime de comportamento. Opcional pelo mesmo motivo: uma tela sem regra
   * nenhuma não deve pagar por ele.
   */
  private readonly runtime = inject(SCREEN_RUNTIME, { optional: true });

  /**
   * O documento inteiro, para saber se este nó é um painel que a grade leva
   * para outro lugar. Opcional: fora de uma tela montada pelo motor — um teste,
   * por exemplo — não há contexto, e o nó desenha normalmente.
   */
  private readonly context = inject(ScreenContext, { optional: true });

  /**
   * Se o nó aparece.
   *
   * No EDITOR o nó escondido continua sendo desenhado: quem está montando a tela
   * precisa enxergar o campo para configurar a regra que o esconde. Sem isso,
   * criar um `visibleWhen` faria o componente sumir do canvas e não haveria como
   * selecioná-lo de novo.
   */
  readonly visible = computed<boolean>(() => {
    if (this.mode() === 'editPreview') {
      return true;
    }
    if (this.isMovedDetailPanel()) {
      return false;
    }
    if (this.runtime === null) {
      return true;
    }
    return this.runtime.stateOf(this.node().id).visible;
  });

  /**
   * Este painel é desenhado pela grade, em outro lugar?
   *
   * Nos modos `drawer` e `flip` o painel de detalhe sai do lugar de origem —
   * ver `detail-panels.ts`. Desenhá-lo aqui TAMBÉM criaria duas cópias vivas dos
   * mesmos controles, e o usuário editaria uma sem ver a outra.
   */
  private readonly isMovedDetailPanel = computed<boolean>(() => {
    if (this.context === null) {
      return false;
    }

    return movedDetailPanelIds(this.context.document()).has(this.node().id);
  });

  /**
   * A sobreposição que o editor quer neste nó, se quiser alguma.
   *
   * Era o gancho que a Fase 3 desenhou e que até agora devolvia `null` sempre —
   * é por ele que a alça de redimensionar entra, sem o renderer saber que ela
   * existe.
   */
  readonly overlayType = computed<Type<unknown> | null>(() => {
    if (this.chrome === null) {
      return null;
    }
    return this.chrome.overlayFor(this.node());
  });

  /** O nó, que é tudo o que uma sobreposição precisa saber. */
  readonly overlayInputs = computed<Readonly<Record<string, unknown>>>(
    () => ({ node: this.node() })
  );

  /** Classes que o editor pede. Vazio quando não há editor. */
  readonly chromeClasses = computed<Readonly<Record<string, boolean>>>(() => {
    if (this.chrome === null) {
      return {};
    }
    return this.chrome.decorate(this.node());
  });

  /**
   * Qual componente desenha este nó.
   *
   * O editor pode devolver um marcador no lugar do componente real — é o que
   * evita, por exemplo, que um `dashboardEmbed` dispare as consultas do
   * dashboard a cada montagem no canvas, ou que um `customComponent` execute
   * código de terceiro no meio da edição.
   */
  readonly rendererType = computed<Type<unknown> | null>(() => {
    const type = this.node().type;

    if (this.chrome !== null && this.mode() === 'editPreview') {
      const placeholder = this.chrome.placeholderFor(type);
      if (placeholder !== null) {
        return placeholder;
      }
    }

    const renderer = this.renderers[type];
    if (renderer === undefined) {
      // Tipo sem renderer não derruba a tela: o nó simplesmente não aparece.
      // `renderer-map.spec.ts` garante que isso não acontece com tipo nativo.
      return null;
    }

    return renderer;
  });

  /**
   * As entradas do renderer, sempre as mesmas três.
   *
   * `computed` memoiza por identidade, então o objeto só é recriado quando um
   * dos três muda de fato — e não a cada ciclo de detecção, que é o padrão que
   * já travou outras telas deste projeto.
   */
  readonly rendererInputs = computed<NodeRendererInputs>(() => ({
    node: this.node(),
    form: this.form(),
    mode: this.mode()
  }));

  /**
   * Deixa o editor consumir o gesto antes do componente.
   *
   * `pointerdown`, e não `click`: no canvas, clicar num select precisa
   * SELECIONAR o nó, não abrir a lista de opções. Esperar o `click` deixaria o
   * componente reagir primeiro, e o usuário veria a lista abrir a cada tentativa
   * de selecionar o campo.
   */
  onPointerDown(event: PointerEvent): void {
    if (this.chrome === null) {
      return;
    }

    const consumed = this.chrome.interceptPointer(this.node(), event);

    if (consumed) {
      // Para a propagação: sem isso, o clique num campo dentro de um painel
      // selecionaria o campo E o painel, e o último a responder venceria.
      event.stopPropagation();
      event.preventDefault();
    }
  }
}
